/**
 * Canonical key for comparing exercise names across stored data, imports, and user input.
 * Normalizes case, hyphens/dashes, punctuation, and spacing so e.g. "Low-Bar Squat" matches
 * "Low Bar Squat".
 */
export function canonicalExerciseKey(s) {
  if (s == null || s === "") return "";
  let t = String(s).normalize("NFKC");
  t = t.replace(/[\u200B-\u200D\uFEFF]/g, ""); // zero-width / BOM
  t = t.replace(/\u00a0/g, " "); // non-breaking space → space
  t = t.toLowerCase().trim();
  // Hyphens, minus, en/em dashes → space (common in "low-bar" vs "low bar")
  t = t.replace(/[\u2013\u2014\u2212\u002d\uFE63\uFF0D]/g, " ");
  t = t.replace(/[''`´]/g, "");
  // Non letters/digits → space (keeps unicode letters via \p{L} if supported)
  try {
    t = t.replace(/[^\p{L}\p{N}]+/gu, " ");
  } catch {
    t = t.replace(/[^a-z0-9]+/gi, " ");
  }
  t = t.replace(/\s+/g, " ").trim();
  return t;
}

/** True iff both names normalize to the same non-empty canonical key. */
export function exerciseNamesMatch(a, b) {
  const ca = canonicalExerciseKey(a);
  const cb = canonicalExerciseKey(b);
  return Boolean(ca && cb && ca === cb);
}

export function canonicalNameSetFromStrings(names) {
  const set = new Set();
  if (!Array.isArray(names)) return set;
  for (const n of names) {
    const k = canonicalExerciseKey(n);
    if (k) set.add(k);
  }
  return set;
}

/** Collect typed exercise names from react-final-form workout values. */
export function collectExerciseNamesFromFormExercises(exercises) {
  const names = [];
  if (!Array.isArray(exercises)) return names;
  for (const ex of exercises) {
    if (!ex) continue;
    const t = String(ex.exerciseType || "single").toLowerCase();
    const hasCircuitData =
      Array.isArray(ex.circuit) &&
      ex.circuit.some((r) => Array.isArray(r) && r.length > 0);
    if (t === "circuit" || hasCircuitData) {
      if (Array.isArray(ex.circuit)) {
        for (const round of ex.circuit) {
          if (!Array.isArray(round)) continue;
          for (const sub of round) {
            const n = sub?.name;
            if (n != null && String(n).trim()) names.push(String(n).trim());
          }
        }
      }
      const parent = ex.name;
      if (parent != null && String(parent).trim()) names.push(String(parent).trim());
    } else {
      const n = ex.name;
      if (n != null && String(n).trim()) names.push(String(n).trim());
    }
  }
  return names;
}
